// Notice type for app notice rules
export type NoticeType = "ANNOUNCEMENT" | "MAINTENANCE";

export const NoticeTypeEnum = {
  ANNOUNCEMENT: "ANNOUNCEMENT",
  MAINTENANCE: "MAINTENANCE",
} as const;

// Supported environments for AppNotice rules (matches VITE_ENV values used in the platform)
export type AppNoticeEnvironment = "local" | "dev" | "qa" | "prod";

export const APP_NOTICE_ENVIRONMENTS: AppNoticeEnvironment[] = [
  "local",
  "dev",
  "qa",
  "prod",
];

// Supported countries for AppNotice rules
export type AppNoticeCountry = "senegal" | "tchad";

export const APP_NOTICE_COUNTRIES: AppNoticeCountry[] = ["senegal", "tchad"];

// DTO returned by the BFF (camelCase on the wire — the BFF's own
// request/response bodies are camelCase; only BFF<->API traffic is
// snake_case. Note: the /resolve endpoint's `feature_type` query
// param is a literal @RequestParam name on the BFF and is unaffected
// by this — only JSON body field names follow camelCase).
export interface AppNoticeDto {
  id: number;
  featureType: NoticeType;
  environment: AppNoticeEnvironment | null;
  country: AppNoticeCountry | null;
  enabled: boolean;
  validFrom: string | null;
  validUntil: string | null;
  createdBy: number | null;
  /**
   * MED-1454: display name of `createdBy`, so the audit line can name the author
   * instead of printing the raw id. Absent or null when no name is recorded.
   */
  createdByName?: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  messageEn: string | null;
  messageFr: string | null;
  messageAr: string | null;
  bgColor: string | null;
}

// Wire shape returned by the BFF
export interface AppNoticeWire {
  id: number;
  featureType: NoticeType;
  environment: string | null;
  country: string | null;
  enabled: boolean;
  validFrom: string | null;
  validUntil: string | null;
  createdBy: number | null;
  createdByName?: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  messageEn: string | null;
  messageFr: string | null;
  messageAr: string | null;
  bgColor: string | null;
}

// Payload sent on create / update (camelCase to match the BFF contract)
export interface AppNoticePayload {
  featureType: NoticeType;
  environment: string | null;
  country: string | null;
  enabled: boolean;
  validFrom: string | null;
  validUntil: string | null;
  messageEn: string | null;
  messageFr: string | null;
  messageAr: string | null;
  bgColor: string | null;
}

// VM used inside the form & redux state (camelCase)
export interface AppNoticeInput {
  featureType: NoticeType;
  environment: AppNoticeEnvironment | null;
  country: AppNoticeCountry | null;
  enabled: boolean;
  validFrom: string | null;
  validUntil: string | null;
  messageEn: string | null;
  messageFr: string | null;
  messageAr: string | null;
  bgColor: string | null;
}

// ── Mappers ───────────────────────────────────────────────────────────────

export function fromWire(wire: AppNoticeWire): AppNoticeDto {
  return {
    id: wire.id,
    featureType: wire.featureType,
    environment: (wire.environment as AppNoticeEnvironment | null) ?? null,
    country: (wire.country as AppNoticeCountry | null) ?? null,
    enabled: wire.enabled,
    validFrom: wire.validFrom,
    validUntil: wire.validUntil,
    createdBy: wire.createdBy,
    createdByName: wire.createdByName ?? null,
    createdAt: wire.createdAt,
    updatedAt: wire.updatedAt,
    messageEn: wire.messageEn,
    messageFr: wire.messageFr,
    messageAr: wire.messageAr,
    bgColor: wire.bgColor,
  };
}

export function toPayload(input: AppNoticeInput): AppNoticePayload {
  return {
    featureType: input.featureType,
    environment: input.environment ?? null,
    country: input.country ?? null,
    enabled: input.enabled,
    validFrom: input.validFrom ?? null,
    validUntil: input.validUntil ?? null,
    messageEn: input.messageEn ?? null,
    messageFr: input.messageFr ?? null,
    messageAr: input.messageAr ?? null,
    bgColor: input.bgColor ?? null,
  };
}
